import { useCallback, useEffect, useRef } from 'react';

/**
 * Custom hook for panning and zooming logic in the Node Editor
 */
export const usePanningAndZoom = ({
  isPanning,
  setIsPanning,
  lastPanPoint,
  setLastPanPoint,
  panOffset,
  setPanOffset,
  zoom,
  setZoom,
  lockedZoomCenter,
  setLockedZoomCenter,
  flowAreaRef,
  currentAction,
  setCurrentAction,
  ACTION_STATE
}) => {
  // Drag state stored entirely in a ref — never triggers React re-renders.
  // lastX/lastY track the live position so we can commit to React state on mouseUp.
  const drag = useRef({
    active: false,
    startX: 0, startY: 0,
    originX: 0, originY: 0,
    lastX: 0, lastY: 0,
    zoom: 1,
  });

  // Find the .flow-container element (child of flowAreaRef) to update its
  // transform directly without going through React state during the drag.
  const getFlowContainer = useCallback(() => {
    return flowAreaRef.current?.querySelector('.flow-container') || null;
  }, [flowAreaRef]);

  const handleMouseDown = (e) => {
    // Only start panning if we're in IDLE state
    if (currentAction && ACTION_STATE && currentAction !== ACTION_STATE.IDLE) {
      return;
    }
    
    // Only start panning if clicking directly on the canvas background (not on panels or components)
    const isPanel = e.target.closest('.flow-panel');
    const isComponent = e.target.closest('.component-item');
    
    if (isPanel || isComponent) {
      return;
    }

    if (e.button === 0) {
      if (setCurrentAction && ACTION_STATE) {
        setCurrentAction(ACTION_STATE.PANNING);
      }
      setIsPanning(true);
      drag.current = {
        active: true,
        startX: e.clientX,
        startY: e.clientY,
        originX: panOffset.x,
        originY: panOffset.y,
        lastX: panOffset.x,
        lastY: panOffset.y,
        zoom,
      };
      setLastPanPoint({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e) => {
    if (!drag.current.active) return;

    const newX = drag.current.originX + (e.clientX - drag.current.startX);
    const newY = drag.current.originY + (e.clientY - drag.current.startY);

    drag.current.lastX = newX;
    drag.current.lastY = newY;

    // Update the DOM transform directly — zero React re-renders during drag,
    // giving the same 1:1 cursor feel as the Component Flow canvas.
    const container = getFlowContainer();
    if (container) {
      container.style.transform = `translate(${newX}px, ${newY}px) scale(${drag.current.zoom})`;
    }
    const area = flowAreaRef.current;
    if (area) {
      area.style.setProperty('--grid-x', `${newX}px`);
      area.style.setProperty('--grid-y', `${newY}px`);
    }
  };

  const handleMouseUp = () => {
    if (drag.current.active) {
      // Commit the final position back to React state so all dependent
      // calculations (hit-testing, zoom origin, etc.) stay accurate.
      setPanOffset({ x: drag.current.lastX, y: drag.current.lastY });
    }
    drag.current.active = false;
    setIsPanning(false);
    if (setCurrentAction && ACTION_STATE) {
      setCurrentAction(ACTION_STATE.IDLE);
    }
  };

  const handleSliderZoom = useCallback((newZoom) => {
    if (!flowAreaRef.current) return;
    const rect = flowAreaRef.current.getBoundingClientRect();
    
    // Lock zoom center at middle of viewport if not already locked
    if (!lockedZoomCenter) {
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      setLockedZoomCenter({ x: centerX, y: centerY });
      handleLockedZoom(newZoom, { x: centerX, y: centerY });
    } else {
      handleLockedZoom(newZoom, lockedZoomCenter);
    }
  }, [lockedZoomCenter, flowAreaRef]);

  const handleLockedZoom = useCallback((newZoom, lockedCenter) => {
    const zoomFactor = newZoom / zoom;
    const { x: centerX, y: centerY } = lockedCenter;
    const worldX = (centerX - panOffset.x) / zoom;
    const worldY = (centerY - panOffset.y) / zoom;
    setPanOffset({ x: centerX - worldX * newZoom, y: centerY - worldY * newZoom });
    setZoom(newZoom);
  }, [zoom, panOffset, setPanOffset, setZoom]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    if (!flowAreaRef.current) return;
    const rect = flowAreaRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const delta = e.deltaY < 0 ? 1.1 : 0.9;
    const newZoom = Math.max(0.5, Math.min(2, zoom * delta));
    const zoomFactor = newZoom / zoom;
    const worldX = (mouseX - panOffset.x) / zoom;
    const worldY = (mouseY - panOffset.y) / zoom;
    setPanOffset({ x: mouseX - worldX * newZoom, y: mouseY - worldY * newZoom });
    setZoom(newZoom);
  }, [zoom, panOffset, setPanOffset, setZoom, flowAreaRef]);

  // Set up global mouse event listeners for panning.
  // handleMouseMove/handleMouseUp only read from the `drag` ref (not React state),
  // so they are stable — we only need to add/remove them when isPanning toggles.
  useEffect(() => {
    if (isPanning) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isPanning]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set up wheel event listener with passive: false to allow preventDefault
  useEffect(() => {
    const element = flowAreaRef.current;
    if (!element) return;

    element.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      element.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel, flowAreaRef]);

  return {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleSliderZoom,
    handleLockedZoom,
    handleWheel,
  };
};
